import AsyncStorage from '@react-native-async-storage/async-storage';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { captureException } from '@/utils/sentry';
import { getSavedUserSettings } from './user-context';
import { ItemSortOrder } from './item-context-types';
import { OrderGuideSortOrder } from '@/types';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

interface Props {
  children?: React.ReactNode;
}

export enum ListDisplaySize {
  Condensed = 'CONDENSED',
  Huge = 'HUGE',
  Large = 'LARGE',
  Medium = 'MEDIUM',
  Small = 'SMALL',
  Standard = 'STANDARD',
}

export interface OrderGuideSettingsType {
  listDisplaySize: ListDisplaySize;
  orderGuideSortOrder: OrderGuideSortOrder;
  selectedCollectionUUID: string | null;
  showCategoryFilters: boolean | null;
  userHasSetSortOrder?: boolean;
}

export const INITIAL_ORDER_GUIDE_SETTINGS: OrderGuideSettingsType = {
  listDisplaySize: ListDisplaySize.Standard,
  orderGuideSortOrder: OrderGuideSortOrder.Alphabetical,
  selectedCollectionUUID: null,
  showCategoryFilters: null,
};

type SavedSettingsType = Partial<OrderGuideSettingsType> & {
  showCondensedView?: boolean;
};

export interface OrderGuideSettingsContextType {
  isHydrated: boolean;
  listDisplaySize: ListDisplaySize;
  orderGuideSortOrder: OrderGuideSortOrder;
  selectedCollectionUUID: string | null;
  setListDisplaySize: (listDisplaySize: ListDisplaySize) => void;
  setOrderGuideSortOrder: (sortOrder: OrderGuideSortOrder) => void;
  setSelectedCollectionUUID: (collectionUUID: string | null) => void;
  setShowCategoryFilters: (nextValue: boolean) => void;
  showCategoryFilters: boolean;
}

export const OrderGuideSettingsContext =
  createContext<OrderGuideSettingsContextType | null>(null);

export const ORDER_GUIDE_SETTINGS_KEY = 'orderGuideSettings';

// Order frequency cannot be selected as part of the query, so it's excluded
// Custom first for UI preference
export const MULTI_ORDER_GUIDE_ITEM_SORT_ORDERS: OrderGuideSortOrder[] = [
  OrderGuideSortOrder.Custom,
  OrderGuideSortOrder.Alphabetical,
  OrderGuideSortOrder.AlphabeticalReverse,
  OrderGuideSortOrder.ItemCode,
  OrderGuideSortOrder.ItemCodeReverse,
  OrderGuideSortOrder.PurchasedDate,
];
const toValidSortOrder = (value?: ItemSortOrder): OrderGuideSortOrder =>
  MULTI_ORDER_GUIDE_ITEM_SORT_ORDERS.find((order) => order === value) ??
  OrderGuideSortOrder.Custom;

export const OrderGuideSettingsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [orderGuideSettings, setOrderGuideSettings] =
    useState<OrderGuideSettingsType>(INITIAL_ORDER_GUIDE_SETTINGS);

  const defaultSortOrder = useDynamicConfigValue(
    'order_guide_default_sort_order',
  );

  useEffect(() => {
    const checkAndHydrateSettings = async () => {
      try {
        let nextOrderGuideSettings = INITIAL_ORDER_GUIDE_SETTINGS;
        const savedOrderGuideSettings = await AsyncStorage.getItem(
          ORDER_GUIDE_SETTINGS_KEY,
        );
        if (savedOrderGuideSettings) {
          const parsedSettings: SavedSettingsType = JSON.parse(
            savedOrderGuideSettings,
          );
          // If the flag is absent, infer from whether the saved sort order
          // differs from the old hardcoded default (Alphabetical).
          const userHasSetSortOrder =
            parsedSettings.userHasSetSortOrder ??
            parsedSettings.orderGuideSortOrder !==
              OrderGuideSortOrder.Alphabetical;
          nextOrderGuideSettings = {
            ...INITIAL_ORDER_GUIDE_SETTINGS,
            listDisplaySize: parsedSettings.showCondensedView
              ? ListDisplaySize.Condensed
              : ListDisplaySize.Standard,
            ...parsedSettings,
            orderGuideSortOrder: userHasSetSortOrder
              ? toValidSortOrder(parsedSettings.orderGuideSortOrder)
              : (defaultSortOrder ?? OrderGuideSortOrder.Alphabetical),
            userHasSetSortOrder,
          };
        } else {
          // Backfill settings from old UserSettingsContext
          // Directly read from AsyncStorage instead of old UserSettingsContext
          // because first render always returns initial value of Alphabetical
          // regardless of what's saved
          const savedSettings = await getSavedUserSettings();
          nextOrderGuideSettings = {
            ...INITIAL_ORDER_GUIDE_SETTINGS,
            orderGuideSortOrder:
              defaultSortOrder ??
              toValidSortOrder(savedSettings.orderGuideSortOrder),
          };
        }
        setOrderGuideSettings(nextOrderGuideSettings);
        await AsyncStorage.setItem(
          ORDER_GUIDE_SETTINGS_KEY,
          JSON.stringify(nextOrderGuideSettings),
        );
      } catch (e) {
        // ensure sort order is set if there is an error
        captureException(e);
        setOrderGuideSettings((prev) => ({
          ...prev,
          orderGuideSortOrder:
            defaultSortOrder ?? OrderGuideSortOrder.Alphabetical,
        }));
      }
      setIsHydrated(true);
    };

    checkAndHydrateSettings();
  }, [defaultSortOrder]);

  const setOrderGuideSettingsCallback = useCallback(
    async (settings: Partial<OrderGuideSettingsType>) => {
      try {
        const savedOrderGuideSettings = await AsyncStorage.getItem(
          ORDER_GUIDE_SETTINGS_KEY,
        );
        const nextOrderGuideSettings = {
          ...(savedOrderGuideSettings
            ? JSON.parse(savedOrderGuideSettings)
            : INITIAL_ORDER_GUIDE_SETTINGS),
          ...settings,
        };

        await AsyncStorage.setItem(
          ORDER_GUIDE_SETTINGS_KEY,
          JSON.stringify(nextOrderGuideSettings),
        );

        setOrderGuideSettings(nextOrderGuideSettings);
      } catch (e) {
        captureException(e);
      }
    },
    [],
  );

  const settingsContextValue = useMemo<OrderGuideSettingsContextType>(() => {
    return {
      isHydrated,
      listDisplaySize: orderGuideSettings.listDisplaySize,
      orderGuideSortOrder: orderGuideSettings.orderGuideSortOrder,
      selectedCollectionUUID: orderGuideSettings.selectedCollectionUUID,
      setListDisplaySize: (listDisplaySize: ListDisplaySize) => {
        setOrderGuideSettingsCallback({
          listDisplaySize,
        });
      },
      setOrderGuideSortOrder: (sortOrder: OrderGuideSortOrder) => {
        setOrderGuideSettingsCallback({
          orderGuideSortOrder: sortOrder,
          userHasSetSortOrder: true,
        });
      },
      setSelectedCollectionUUID: (collectionUUID: string | null) => {
        setOrderGuideSettingsCallback({
          selectedCollectionUUID: collectionUUID,
        });
      },
      setShowCategoryFilters: (nextValue: boolean) => {
        setOrderGuideSettingsCallback({
          showCategoryFilters: nextValue,
        });
      },
      showCategoryFilters: Boolean(orderGuideSettings.showCategoryFilters),
    };
  }, [isHydrated, orderGuideSettings, setOrderGuideSettingsCallback]);

  return (
    <OrderGuideSettingsContext.Provider value={settingsContextValue}>
      {children}
    </OrderGuideSettingsContext.Provider>
  );
};

export const DummyOrderGuideSettingsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const settingsContextValue = {
    isHydrated: false,
    listDisplaySize: ListDisplaySize.Standard,
    orderGuideSortOrder: OrderGuideSortOrder.Alphabetical,
    selectedCollectionUUID: null,
    setListDisplaySize: () => {},
    setOrderGuideSortOrder: () => {},
    setSelectedCollectionUUID: () => {},
    setShowCategoryFilters: () => {},
    showCategoryFilters: false,
  };
  return (
    <OrderGuideSettingsContext.Provider value={settingsContextValue}>
      {children}
    </OrderGuideSettingsContext.Provider>
  );
};

export const useOrderGuideSettingsContext = () => {
  const context = useContext(OrderGuideSettingsContext);
  if (!context) {
    throw new Error(
      'useOrderGuideSettingsContext requires a OrderGuideSettingsContext',
    );
  }

  return context;
};
